import { useState, useRef, useCallback, startTransition } from 'react';
import { logGuessSubmitted, logGuessCorrect, logGuessIncorrect, logGameCompleted } from '../components/analytics';

interface GuessFormState {
  firstName: string;
  middleName: string;
  lastName: string;
  gameCompleted: boolean;
  gameWon: boolean;
  score: number;
  guessAttempts: number;
}

interface GuessFormReturn {
  guessState: GuessFormState;
  setGuessState: React.Dispatch<React.SetStateAction<GuessFormState>>;
  guessError: string;
  setGuessError: React.Dispatch<React.SetStateAction<string>>;
  isSubmitting: boolean;
  handleNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (
    event: React.MouseEvent | React.FormEvent,
    targetFirstName?: string,
    targetMiddleName?: string,
    targetLastName?: string,
    recordGuess?: (guessData: any) => Promise<number>,
    updateGameCompletion?: (completed: boolean) => void
  ) => Promise<void>;
}

const normalizeString = (str: string) => {
  return str
    .replace(/\s+/g, '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[''`´-]/g, '')
    .replace(/[""]/g, '')
    .replace(/[–—]/g, '')
    .replace(/[^\w]/g, '');
};

export const useGuessForm = (): GuessFormReturn => {
  const [guessState, setGuessState] = useState<GuessFormState>({
    firstName: '',
    middleName: '',
    lastName: '',
    gameCompleted: false,
    gameWon: false,
    score: 0,
    guessAttempts: 0
  });

  const [guessError, setGuessError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionInProgress = useRef(false);

  const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setGuessState(prev => ({
      ...prev,
      [event.target.name]: event.target.value
    }));

    if (guessError) {
      setGuessError('');
    }
  }, [guessError]);

  const validateFields = useCallback((
    targetFirstName?: string,
    targetMiddleName?: string,
    targetLastName?: string
  ) => {
    if (!guessState.firstName.trim() && targetFirstName) {
      setGuessError('First name is required');
      return false;
    }
    if (!guessState.middleName.trim() && targetMiddleName) {
      setGuessError('Middle name is required');
      return false;
    }
    if (!guessState.lastName.trim() && targetLastName) {
      setGuessError('Last name is required');
      return false;
    }
    return true;
  }, [guessState.firstName, guessState.middleName, guessState.lastName]);

  const handleSubmit = useCallback(async (
    event: React.MouseEvent | React.FormEvent,
    targetFirstName?: string,
    targetMiddleName?: string,
    targetLastName?: string,
    recordGuess?: (guessData: any) => Promise<number>,
    updateGameCompletion?: (completed: boolean) => void
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (guessState.gameCompleted || submissionInProgress.current || isSubmitting) {
      return;
    }

    if (!validateFields(targetFirstName, targetMiddleName, targetLastName)) {
      return;
    }

    submissionInProgress.current = true;
    setIsSubmitting(true);
    setGuessError('');

    const guessData = {
      firstname: guessState.firstName,
      middlename: guessState.middleName,
      lastname: guessState.lastName
    };

    const fullGuess = guessState.firstName.concat(
      targetMiddleName ? ' ' : '',
      guessState.middleName,
      ' ',
      guessState.lastName
    );

    const newAttempts = guessState.guessAttempts + 1;
    logGuessSubmitted(fullGuess);

    // OPTIMISTIC UPDATE: Validate the guess immediately before making the API call
    const isCorrectGuess =
      normalizeString(guessState.firstName) === normalizeString(targetFirstName || '') &&
      normalizeString(guessState.middleName) === normalizeString(targetMiddleName || '') &&
      normalizeString(guessState.lastName) === normalizeString(targetLastName || '');

    // Calculate optimistic score (add 10 points for guess)
    const GUESS_VALUE = 10;
    const optimisticScore = guessState.score + GUESS_VALUE;

    // Update UI immediately with optimistic state
    if (isCorrectGuess) {
      const targetFullName = `${targetFirstName || ''} ${targetMiddleName || ''} ${targetLastName || ''}`.trim();
      logGuessCorrect(targetFullName, optimisticScore, newAttempts);
      logGameCompleted(optimisticScore);

      setGuessState(prev => ({
        ...prev,
        gameCompleted: true,
        gameWon: true,
        score: optimisticScore,
        guessAttempts: newAttempts
      }));

      setGuessError('');

      if (updateGameCompletion) {
        updateGameCompletion(true);
      }
    } else {
      const targetFullName = `${targetFirstName || ''} ${targetMiddleName || ''} ${targetLastName || ''}`.trim();
      logGuessIncorrect(fullGuess, targetFullName);

      setGuessState(prev => ({
        ...prev,
        score: optimisticScore,
        guessAttempts: newAttempts
      }));

      setGuessError('Incorrect, try again!');
    }

    // Reset UI loading state immediately, but keep submission lock until API completes
    setIsSubmitting(false);

    // Make the API call in the background and reconcile if needed
    try {
      if (recordGuess) {
        const serverScore = await recordGuess(guessData);

        // Reconcile: if server score differs from optimistic score, update it
        // Use startTransition to mark this as a non-urgent update to prevent ResizeObserver errors
        // See src/PERFORMANCE.md for details on why this approach is necessary
        if (serverScore !== optimisticScore) {
          console.log('Reconciling score:', { optimistic: optimisticScore, server: serverScore });
          startTransition(() => {
            setGuessState(prev => ({
              ...prev,
              score: serverScore
            }));
          });
        }
      }
    } catch (error) {
      console.error('Error recording guess through GameManager:', error);
      // Keep optimistic updates even if server call fails
      // The game will still be playable and the data will sync later
    } finally {
      // Release submission lock after API call completes
      submissionInProgress.current = false;
    }
  }, [guessState, isSubmitting, validateFields]);

  return {
    guessState,
    setGuessState,
    guessError,
    setGuessError,
    isSubmitting,
    handleNameChange,
    handleSubmit
  };
};